import { useEffect, useState, useMemo, useRef } from "react";
import { Plus, ChevronDown, ChevronRight, Loader2, Maximize2, ListChecks, Archive, ArchiveRestore, Eye, EyeOff, FolderOpen } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";
import { useRealtimeChannel } from "@/context/RealtimeContext";
import FocusHeader from "@/components/FocusHeader";
import StatusPill from "@/components/StatusPill";
import PriorityPill from "@/components/PriorityPill";
import ItemModal from "@/components/ItemModal";
import GroupModal from "@/components/GroupModal";
import TaskExpandOverlay from "@/components/TaskExpandOverlay";
import OwnerAvatar from "@/components/OwnerAvatar";
import SyncStatusBadge from "@/components/SyncStatusBadge";

/**
 * Monday.com-style compact board. Optimised for a 60" always-on TV:
 *  - Dense 32px rows so most tasks fit above the fold.
 *  - Every task carries an index badge (#01, #02...) matched by voice command.
 *  - Listens for the `virtus:expand-task` window event fired by the Voice Orb.
 */
export default function BoardScreen({ board, code, title, subtitle, seedGroups = [] }) {
    const nav = useNavigate();
    const [groups, setGroups] = useState([]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [collapsed, setCollapsed] = useState({});
    const [modalItem, setModalItem] = useState(null);
    const [showItemModal, setShowItemModal] = useState(false);
    const [modalGroup, setModalGroup] = useState(null);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingValue, setEditingValue] = useState("");
    const [expandedTask, setExpandedTask] = useState(null);
    const [showArchive, setShowArchive] = useState(false);
    const flatOrderedRef = useRef([]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/boards/${board}`, {
                params: { include_archived: showArchive },
            });
            let g = data.groups || [];
            if (g.length === 0 && seedGroups.length > 0) {
                for (const s of seedGroups) {
                    await api.post(`/boards/${board}/groups`, s);
                }
                const { data: d2 } = await api.get(`/boards/${board}`, {
                    params: { include_archived: showArchive },
                });
                g = d2.groups || [];
                setItems(d2.items || []);
            } else {
                setItems(data.items || []);
            }
            setGroups(g);
        } catch (e) {
            toast.error("Failed to load board.");
        } finally {
            setLoading(false);
        }
    };

    useRealtimeChannel("boards", load);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [board, showArchive]);

    /** Flat list in display order (group by group), used for index numbering
     *  and voice "expand task N" matching. Archived items get their own index space. */
    const { orderedItems, orderedArchived } = useMemo(() => {
        const active = [];
        const archived = [];
        for (const g of groups) {
            const gi = items
                .filter((i) => i.group_id === g.id)
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            for (const it of gi) {
                if (it.archived) archived.push({ ...it, __group: g });
                else active.push({ ...it, __group: g });
            }
        }
        return {
            orderedItems: active.map((x, i) => ({ ...x, index: i + 1 })),
            orderedArchived: archived.map((x, i) => ({ ...x, index: i + 1, __archived: true })),
        };
    }, [items, groups]);

    // Keep ref in sync for event handler.
    useEffect(() => {
        flatOrderedRef.current = orderedItems;
    }, [orderedItems]);

    const itemsByGroup = useMemo(() => {
        const map = {};
        for (const g of groups) map[g.id] = [];
        for (const it of orderedItems) {
            if (!map[it.group_id]) map[it.group_id] = [];
            map[it.group_id].push(it);
        }
        return map;
    }, [orderedItems, groups]);

    // Listen for global voice-triggered expand / close events.
    useEffect(() => {
        const onExpand = (e) => {
            const n = Number(e.detail?.index);
            const list = flatOrderedRef.current;
            if (!n || !list.length) {
                toast.warning(`Task #${n} not found on this board.`);
                return;
            }
            const target = list.find((x) => x.index === n);
            if (!target) {
                toast.warning(`Task #${n} not found. Board has ${list.length} tasks.`);
                return;
            }
            setExpandedTask(target);
        };
        const onClose = () => setExpandedTask(null);
        window.addEventListener("virtus:expand-task", onExpand);
        window.addEventListener("virtus:collapse-task", onClose);
        return () => {
            window.removeEventListener("virtus:expand-task", onExpand);
            window.removeEventListener("virtus:collapse-task", onClose);
        };
    }, []);

    const openNewItem = (group_id) => {
        setModalItem({ group_id, status: "planning", priority: "medium" });
        setShowItemModal(true);
    };

    const openEditItem = (item) => {
        setModalItem(item);
        setShowItemModal(true);
    };

    // Keep expandedTask in sync with the latest items list so subtask
    // mutations reflect immediately in the overlay.
    const currentExpanded = useMemo(() => {
        if (!expandedTask) return null;
        const fresh = orderedItems.find((x) => x.id === expandedTask.id);
        return fresh || expandedTask;
    }, [expandedTask, orderedItems]);

    // --- Subtasks --------------------------------------------------------
    const applyItemUpdate = (updated) => {
        setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    };

    const addSubtask = async (name) => {
        if (!expandedTask?.id) return;
        try {
            const { data: updated } = await api.post(
                `/boards/${board}/items/${expandedTask.id}/subtasks`,
                { name }
            );
            applyItemUpdate(updated);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Add subtask failed.");
        }
    };

    const toggleSubtask = async (subtaskId, done) => {
        if (!expandedTask?.id) return;
        try {
            const { data: updated } = await api.patch(
                `/boards/${board}/items/${expandedTask.id}/subtasks/${subtaskId}`,
                { done }
            );
            applyItemUpdate(updated);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update subtask failed.");
        }
    };

    const renameSubtask = async (subtaskId, name) => {
        if (!expandedTask?.id) return;
        try {
            const { data: updated } = await api.patch(
                `/boards/${board}/items/${expandedTask.id}/subtasks/${subtaskId}`,
                { name }
            );
            applyItemUpdate(updated);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Rename subtask failed.");
        }
    };

    const deleteSubtask = async (subtaskId) => {
        if (!expandedTask?.id) return;
        try {
            const { data: updated } = await api.delete(
                `/boards/${board}/items/${expandedTask.id}/subtasks/${subtaskId}`
            );
            applyItemUpdate(updated);
        } catch (e) {
            toast.error("Delete subtask failed.");
        }
    };

    const restoreItem = async (itemId) => {
        try {
            const { data: updated } = await api.post(
                `/boards/${board}/items/${itemId}/restore`
            );
            setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            toast.success("Task restored.");
        } catch (e) {
            toast.error("Restore failed.");
        }
    };

    const saveItem = async (data) => {
        try {
            if (modalItem?.id) {
                const { data: updated } = await api.patch(
                    `/boards/${board}/items/${modalItem.id}`,
                    data
                );
                setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            } else {
                const { data: created } = await api.post(`/boards/${board}/items`, data);
                setItems((prev) => [...prev, created]);
            }
            setShowItemModal(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed.");
        }
    };

    const deleteItem = async () => {
        if (!modalItem?.id) return;
        try {
            await api.delete(`/boards/${board}/items/${modalItem.id}`);
            setItems((prev) => prev.filter((x) => x.id !== modalItem.id));
            setShowItemModal(false);
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    const saveGroup = async (data) => {
        try {
            if (modalGroup?.id) {
                const { data: updated } = await api.patch(
                    `/boards/${board}/groups/${modalGroup.id}`,
                    data
                );
                setGroups((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
            } else {
                const { data: created } = await api.post(`/boards/${board}/groups`, data);
                setGroups((prev) => [...prev, created]);
            }
            setShowGroupModal(false);
        } catch (e) {
            toast.error("Save failed.");
        }
    };

    const updateItemStatus = async (item) => {
        const order = ["planning", "working", "stuck", "done", "on-hold"];
        const next = order[(order.indexOf(item.status) + 1) % order.length];
        try {
            const { data: updated } = await api.patch(
                `/boards/${board}/items/${item.id}`,
                { status: next }
            );
            setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
        } catch (e) {
            toast.error("Update failed.");
        }
    };

    const updateItemPriority = async (item) => {
        const order = ["low", "medium", "high", "critical"];
        const next = order[(order.indexOf(item.priority) + 1) % order.length];
        try {
            const { data: updated } = await api.patch(
                `/boards/${board}/items/${item.id}`,
                { priority: next }
            );
            setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
        } catch (e) {
            toast.error("Update failed.");
        }
    };

    const startInlineEdit = (item) => {
        setEditingId(item.id);
        setEditingValue(item.name);
    };

    const commitInlineEdit = async () => {
        const id = editingId;
        const newName = editingValue.trim();
        setEditingId(null);
        if (!id) return;
        const current = items.find((x) => x.id === id);
        if (!current || !newName || newName === current.name) return;
        setItems((prev) => prev.map((x) => (x.id === id ? { ...x, name: newName } : x)));
        try {
            const { data: updated } = await api.patch(`/boards/${board}/items/${id}`, {
                name: newName,
            });
            setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
        } catch (e) {
            toast.error("Rename failed.");
            setItems((prev) => prev.map((x) => (x.id === id ? current : x)));
        }
    };

    const cancelInlineEdit = () => {
        setEditingId(null);
        setEditingValue("");
    };

    const expandItem = (item) => setExpandedTask(item);

    // Metrics reflect the ACTIVE board (archived items are excluded).
    const activeItems = useMemo(() => items.filter((i) => !i.archived), [items]);
    const total = activeItems.length;
    const working = activeItems.filter((i) => i.status === "working").length;
    const stuck = activeItems.filter((i) => i.status === "stuck").length;
    const done = activeItems.filter((i) => i.status === "done").length;

    return (
        <div className="min-h-screen md:h-screen bg-virtus-bg text-white flex flex-col md:overflow-hidden">
            <FocusHeader code={code} title={title} subtitle={subtitle} />

            {/* Compact metrics strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 border-b border-virtus-line flex-shrink-0">
                <Metric label="TOTAL" value={total} color="#FFFFFF" />
                <Metric label="WORKING" value={working} color="#FFEA00" />
                <Metric label="STUCK" value={stuck} color="#FF1744" />
                <Metric label="DONE" value={done} color="#00E676" />
            </div>

            {/* Action strip — stacks on mobile so buttons don't get clipped */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between px-3 md:px-6 py-2 border-b border-virtus-line flex-shrink-0 gap-2 md:gap-0">
                <div className="font-mono text-[10px] tracking-[0.24em] md:tracking-[0.28em] uppercase text-virtus-dim truncate">
                    BOARD // {groups.length} GROUPS · {activeItems.length} TASKS
                    {orderedArchived.length > 0 && (
                        <span className="text-virtus-faint"> · {orderedArchived.length} ARCHIVED</span>
                    )}
                    <span className="hidden md:inline"> · SAY </span>
                    <span className="hidden md:inline text-virtus-orange">"EXPAND TASK N"</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <SyncStatusBadge target={board} />
                    {board === "production" && (
                        <button
                            onClick={() => nav("/production/docs")}
                            className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-1.5 hover:bg-virtus-orange hover:text-black transition-colors"
                            title="Open Production Documents (docs clearance required)"
                            data-testid={TID.prodDocsBtn}
                        >
                            <FolderOpen size={11} /> DOCUMENTS
                        </button>
                    )}
                    <button
                        onClick={() => setShowArchive((v) => !v)}
                        className={`flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 border transition-colors ${
                            showArchive
                                ? "text-virtus-orange border-virtus-orange bg-virtus-orange/10"
                                : "text-virtus-dim border-virtus-line hover:border-virtus-orange hover:text-virtus-orange"
                        }`}
                        title={showArchive ? "Hide archived tasks" : "Show archived tasks"}
                        data-testid="board-toggle-archive-btn"
                    >
                        {showArchive ? <EyeOff size={11} /> : <Eye size={11} />}
                        {showArchive ? "HIDE ARCHIVE" : "SHOW ARCHIVE"}
                    </button>
                    <button
                        onClick={() => {
                            setModalGroup(null);
                            setShowGroupModal(true);
                        }}
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange border border-virtus-orange px-3 py-1.5 hover:bg-virtus-orange hover:text-black transition-colors"
                        data-testid={TID.boardAddGroupBtn}
                    >
                        <Plus size={11} /> ADD GROUP
                    </button>
                </div>
            </div>

            {/* Board content — scroll only inside if it overflows.
                On mobile: also allow horizontal swipe since the row grids are
                ~820px wide and won't fit on a phone. `min-w-[820px] md:min-w-0`
                forces overflow on small screens while keeping full-width on desktop. */}
            <div className="flex-1 overflow-auto px-3 md:px-6 py-3" data-kiosk-scroll="true">
                <div className="min-w-[820px] md:min-w-0">
                {loading ? (
                    <div className="py-16 flex items-center justify-center text-virtus-dim">
                        <Loader2 className="animate-spin" />
                    </div>
                ) : (
                    <div className="space-y-3">
                        {groups.length === 0 && (
                            <div className="border border-dashed border-virtus-line p-10 text-center text-virtus-dim text-sm">
                                No groups yet. Add one to begin.
                            </div>
                        )}
                        {groups.map((g) => {
                            const gi = itemsByGroup[g.id] || [];
                            const isCollapsed = collapsed[g.id];
                            return (
                                <div
                                    key={g.id}
                                    className="border border-virtus-line bg-virtus-surface"
                                    data-testid={TID.boardGroupHeader(g.id)}                                >
                                    {/* Group header — 28px */}
                                    <div
                                        className="flex items-center gap-2 px-3 h-7 border-b border-virtus-line cursor-pointer"
                                        style={{ boxShadow: `inset 3px 0 0 ${g.color}` }}
                                        onClick={() =>
                                            setCollapsed((c) => ({ ...c, [g.id]: !c[g.id] }))
                                        }
                                    >
                                        {isCollapsed ? (
                                            <ChevronRight size={14} className="text-virtus-dim" />
                                        ) : (
                                            <ChevronDown size={14} className="text-virtus-dim" />
                                        )}
                                        <div
                                            className="font-heading font-bold uppercase tracking-tight text-sm"
                                            style={{ color: g.color }}
                                        >
                                            {g.name}
                                        </div>
                                        <div className="font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-faint">
                                            [{gi.length}]
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openNewItem(g.id);
                                            }}
                                            className="ml-auto flex items-center gap-1 font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-dim hover:text-virtus-orange px-2 py-0.5 border border-virtus-line hover:border-virtus-orange"
                                            data-testid={TID.boardAddItemBtn(g.id)}
                                        >
                                            <Plus size={10} /> ADD
                                        </button>
                                    </div>

                                    {!isCollapsed && (
                                        <>
                                            {/* Column headers — 22px */}
                                            <div className="grid grid-cols-[52px_1fr_100px_100px_130px_92px_92px_36px] items-center gap-2 px-3 h-[22px] border-b border-virtus-line font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-faint">
                                                <div>#</div>
                                                <div>TASK</div>
                                                <div>STATUS</div>
                                                <div>PRIORITY</div>
                                                <div>OWNER</div>
                                                <div>START</div>
                                                <div>DUE</div>
                                                <div />
                                            </div>

                                            <AnimatePresence>
                                                {gi.length === 0 && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openNewItem(g.id);
                                                        }}
                                                        className="w-full text-left px-3 py-2 font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-faint hover:text-virtus-orange hover:bg-[#141414] flex items-center gap-2 group/empty"
                                                        data-testid={TID.boardAddFirstItemBtn(g.id)}
                                                    >
                                                        <Plus size={11} className="text-virtus-orange group-hover/empty:text-virtus-orange" />
                                                        <span>&gt; No tasks. </span>
                                                        <span className="text-virtus-orange group-hover/empty:underline">
                                                            Add one.
                                                        </span>
                                                    </button>
                                                )}
                                                {gi.map((item) => {
                                                    const subCount = (item.subtasks || []).length;
                                                    const subDone = (item.subtasks || []).filter((s) => s.done).length;
                                                    const urgency = getDueUrgency(item);
                                                    return (
                                                    <motion.div
                                                        key={item.id}
                                                        initial={{ opacity: 0 }}
                                                        animate={{ opacity: 1 }}
                                                        exit={{ opacity: 0 }}
                                                        transition={{ duration: 0.12 }}
                                                        onClick={() => {
                                                            if (editingId === item.id) return;
                                                            expandItem(item);
                                                        }}
                                                        style={{
                                                            boxShadow: `inset 3px 0 0 ${g.color}`,
                                                        }}
                                                        className="grid grid-cols-[52px_1fr_100px_100px_130px_92px_92px_36px] items-center gap-2 px-3 h-8 border-b border-virtus-line hover:bg-[#141414] cursor-pointer group"
                                                        data-testid={TID.boardItemRow(item.id)}
                                                    >
                                                        <div
                                                            className="font-mono text-[10px] tracking-[0.2em] font-bold text-virtus-orange"
                                                            data-testid={TID.boardItemIndex(item.id)}
                                                        >
                                                            #{String(item.index).padStart(2, "0")}
                                                        </div>
                                                        {editingId === item.id ? (
                                                            <input
                                                                autoFocus
                                                                value={editingValue}
                                                                onChange={(e) =>
                                                                    setEditingValue(e.target.value)
                                                                }
                                                                onClick={(e) => e.stopPropagation()}
                                                                onBlur={commitInlineEdit}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter") {
                                                                        e.preventDefault();
                                                                        commitInlineEdit();
                                                                    } else if (e.key === "Escape") {
                                                                        e.preventDefault();
                                                                        cancelInlineEdit();
                                                                    }
                                                                }}
                                                                className="bg-[#141414] border border-virtus-orange px-2 py-0.5 text-sm text-white outline-none w-full"
                                                                data-testid={`board-item-name-input-${item.id}`}
                                                            />
                                                        ) : (
                                                            <div
                                                                onDoubleClick={(e) => {
                                                                    e.stopPropagation();
                                                                    startInlineEdit(item);
                                                                }}
                                                                className="text-sm text-white group-hover:text-virtus-orange transition-colors truncate px-1 flex items-center gap-2"
                                                                title="Click row to open overview · Double-click name to rename"
                                                                data-testid={`board-item-name-${item.id}`}
                                                            >
                                                                <span className="truncate">{item.name}</span>
                                                                {subCount > 0 && (
                                                                    <span
                                                                        className="flex items-center gap-1 font-mono text-[9px] tracking-[0.18em] uppercase text-virtus-dim border border-virtus-line px-1 py-[1px] flex-shrink-0"
                                                                        title={`${subDone} of ${subCount} subtasks complete`}
                                                                        data-testid={`board-item-sub-count-${item.id}`}
                                                                    >
                                                                        <ListChecks size={10} />
                                                                        {subDone}/{subCount}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                updateItemStatus(item);
                                                            }}
                                                        >
                                                            <StatusPill value={item.status} />
                                                        </div>
                                                        <div
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                updateItemPriority(item);
                                                            }}
                                                        >
                                                            <PriorityPill value={item.priority} />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <OwnerAvatar owner={item.owner} size={22} />
                                                        </div>
                                                        <div className="font-mono text-xs text-virtus-dim">
                                                            {item.start_date || "—"}
                                                        </div>
                                                        <div
                                                            className={`font-mono text-xs px-2 py-[3px] leading-[14px] text-center transition-shadow ${urgency?.pulse ? "urgency-pulse" : ""}`}
                                                            style={
                                                                urgency
                                                                    ? {
                                                                          color: urgency.color,
                                                                          border: `1px solid ${urgency.color}`,
                                                                          backgroundColor: `${urgency.color}14`,
                                                                          boxShadow: `0 0 6px ${urgency.color}66`,
                                                                      }
                                                                    : { color: "#808080" }
                                                            }
                                                            title={urgency?.tooltip}
                                                        >
                                                            {item.due_date || "—"}
                                                        </div>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                expandItem(item);
                                                            }}
                                                            className="opacity-0 group-hover:opacity-100 text-virtus-dim hover:text-virtus-orange transition-opacity"
                                                            title={`Expand task #${item.index}`}
                                                            data-testid={TID.boardItemExpandBtn(item.id)}
                                                            aria-label="Expand task"
                                                        >
                                                            <Maximize2 size={13} />
                                                        </button>
                                                    </motion.div>
                                                    );
                                                })}
                                            </AnimatePresence>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                        {showArchive && orderedArchived.length > 0 && (
                            <ArchiveSection
                                items={orderedArchived}
                                onExpand={(it) => setExpandedTask(it)}
                                onRestore={restoreItem}
                            />
                        )}
                    </div>
                )}
                </div>
            </div>

            <ItemModal
                open={showItemModal}
                item={modalItem}
                groups={groups}
                onClose={() => setShowItemModal(false)}
                onSave={saveItem}
                onDelete={deleteItem}
            />
            <GroupModal
                open={showGroupModal}
                group={modalGroup}
                onClose={() => setShowGroupModal(false)}
                onSave={saveGroup}
            />
            <TaskExpandOverlay
                open={!!expandedTask}
                task={currentExpanded}
                group={currentExpanded?.__group}
                subtasks={currentExpanded?.subtasks || []}
                onClose={() => setExpandedTask(null)}
                onEdit={() => {
                    setModalItem(currentExpanded);
                    setShowItemModal(true);
                    setExpandedTask(null);
                }}
                onAddSubtask={addSubtask}
                onToggleSubtask={toggleSubtask}
                onRenameSubtask={renameSubtask}
                onDeleteSubtask={deleteSubtask}
            />
        </div>
    );
}

function Metric({ label, value, color }) {
    return (
        <div className="px-5 py-2 border-r border-virtus-line last:border-r-0 flex items-baseline gap-4">
            <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-faint">
                {label}
            </div>
            <div
                className="font-mono font-bold text-2xl"
                style={{ color }}
            >
                {String(value).padStart(2, "0")}
            </div>
        </div>
    );
}


/**
 * Returns null when a row should render normally, or an object with color/pulse/tooltip
 * describing how urgent the due-date is. Only fires for active, non-done tasks.
 * yellow ≤ 5 days out · red ≤ 2 days out (or overdue).
 */
function getDueUrgency(item) {
    if (!item || !item.due_date) return null;
    if (item.status === "done") return null;
    if (item.archived) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const due = new Date(item.due_date + "T00:00:00");
    if (isNaN(due.getTime())) return null;
    const days = Math.round((due - now) / (1000 * 60 * 60 * 24));
    if (days <= 2) {
        return {
            color: "#FF1744",
            pulse: true,
            tooltip: days < 0 ? `Overdue by ${Math.abs(days)} day(s)` : `Due in ${days} day(s)`,
        };
    }
    if (days <= 5) {
        return { color: "#FFEA00", pulse: false, tooltip: `Due in ${days} day(s)` };
    }
    return null;
}

function ArchiveSection({ items, onExpand, onRestore }) {
    return (
        <div
            className="border border-virtus-line bg-virtus-surface opacity-90"
            data-testid="board-archive-section"
        >
            <div className="flex items-center gap-2 px-3 h-7 border-b border-virtus-line">
                <Archive size={12} className="text-virtus-faint" />
                <div className="font-mono text-xs tracking-[0.28em] uppercase text-virtus-faint">
                    ARCHIVE
                </div>
                <div className="font-mono text-[10px] text-virtus-dim">[{items.length}]</div>
                <div className="ml-auto font-mono text-[9px] tracking-[0.22em] uppercase text-virtus-faint">
                    Auto-archived after 7 days done
                </div>
            </div>
            <div className="grid grid-cols-[52px_1fr_100px_100px_130px_92px_92px_60px] items-center gap-2 px-3 h-[22px] border-b border-virtus-line font-mono text-[9px] tracking-[0.24em] uppercase text-virtus-faint">
                <div>#</div>
                <div>TASK</div>
                <div>STATUS</div>
                <div>PRIORITY</div>
                <div>OWNER</div>
                <div>START</div>
                <div>DUE</div>
                <div />
            </div>
            {items.map((it) => (
                <div
                    key={it.id}
                    onClick={() => onExpand(it)}
                    className="grid grid-cols-[52px_1fr_100px_100px_130px_92px_92px_60px] items-center gap-2 px-3 h-8 border-b border-virtus-line hover:bg-[#141414] cursor-pointer group"
                    data-testid={`archive-item-row-${it.id}`}
                >
                    <div className="font-mono text-[10px] tracking-[0.2em] font-bold text-virtus-faint">
                        #{String(it.index).padStart(2, "0")}
                    </div>
                    <div className="text-sm text-virtus-dim truncate flex items-center gap-2">
                        <span
                            className="inline-block w-1 h-3"
                            style={{ backgroundColor: it.__group?.color || "#444" }}
                        />
                        <span className="truncate line-through">{it.name}</span>
                    </div>
                    <div className="scale-90 origin-left opacity-70">
                        <StatusPill value={it.status} />
                    </div>
                    <div className="scale-90 origin-left opacity-70">
                        <PriorityPill value={it.priority} />
                    </div>
                    <div className="min-w-0 opacity-70">
                        <OwnerAvatar owner={it.owner} size={20} />
                    </div>
                    <div className="font-mono text-xs text-virtus-faint">
                        {it.start_date || "—"}
                    </div>
                    <div className="font-mono text-xs text-virtus-faint">{it.due_date || "—"}</div>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onRestore(it.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 flex items-center gap-1 font-mono text-[9px] tracking-[0.2em] uppercase text-virtus-orange hover:underline transition-opacity"
                        title="Restore this task to the active board"
                        data-testid={`archive-restore-${it.id}`}
                    >
                        <ArchiveRestore size={11} />
                        RESTORE
                    </button>
                </div>
            ))}
        </div>
    );
}
